import SidePanel from '@monofront/design-system/organisms/SidePanel';
import { DeliveryBarMachineContext } from '@monofront/business/contexts';
import { useTranslations } from 'next-intl';
import { Fragment, useEffect, useMemo, useRef } from 'react';
import {
  SlicesContainer,
  SlicesContainerLayoutType,
} from '@screens/SlicesContainer';
import { ShippingMethodSelector } from './ShippingMethodSelector';
import { DefaultPanelHeader } from '@features/DeliveryBar/Panels/DefaultPanelHeader';
import { growthBookService } from '@services/businessContainer';
import {
  machineSelectedAddressSelector,
  machineValueSelector,
} from '@monofront/business/stateMachines/deliveryBar/selectors';
import { STATES as DeliveryBarState } from '@monofront/business/stateMachines/deliveryBar/states';
import InformativeBox from '@monofront/design-system/components/InformativeBox';
import { useShippingMethodInformationPanel } from './useShippingMethodInformationPanel';
import { Spacer } from './Spacer';
import { EVENTS } from '@monofront/business/stateMachines/deliveryBar/events';
import { trackEvent } from '@monofront/tracking';
import {
  calculateDateDifferenceInDays,
  findClosestAvailableSlotToDate,
} from '@monofront/business/utils';
import { ShippingMethodTypes } from '@monofront/types';

type ShippingMethodSelectionPanelProps = {
  closePanel: () => void;
};

export const ShippingMethodSelectionPanel: React.FC<
  ShippingMethodSelectionPanelProps
> = ({ closePanel }) => {
  const tPanel = useTranslations('DeliveryBar.ShippingMethodSelection');
  const selectedAddress = DeliveryBarMachineContext.useSelector(
    machineSelectedAddressSelector,
  );
  const { send } = DeliveryBarMachineContext.useActorRef();

  const machineValue =
    DeliveryBarMachineContext.useSelector(machineValueSelector);

  const {
    shippingMethodSelectionContent,
    shippingMethods,
    slots,
    descriptionSerializer,
    subtitleSerializer,
    findEligibleShippingMethod,
  } = useShippingMethodInformationPanel();

  useEffect(() => {
    if (!shippingMethods) return;
    growthBookService.setGrowthbookAttributes({
      // put in deliverybarcontext
      eligibleCarriers: shippingMethods.map((el) => el.code),
    });
  }, [shippingMethods]);

  const lastTrackedSlotRef = useRef<number | null>(null);
  const lastTrackedZipcodeRef = useRef<string>('');
  const hasCourier = shippingMethods?.some(
    (shippingMethod) => shippingMethod.type === ShippingMethodTypes.COURIER,
  );

  useEffect(() => {
    const today = new Date();

    const closestSlots =
      shippingMethodSelectionContent?.shippingMethods.reduce(
        (acc: { date: string; days: number }[], { shippingMethod }) => {
          const eligibleShippingMethod = findEligibleShippingMethod(
            shippingMethod.data.shippingMethodType,
          );

          if (!eligibleShippingMethod) {
            return acc;
          }

          if (slots.length === 0) {
            return acc;
          }

          const nextAvailableDayWithSlots = findClosestAvailableSlotToDate(
            slots,
            today,
          );

          if (!nextAvailableDayWithSlots?.date) {
            return acc;
          }

          const nextDayWithSlotInDays = calculateDateDifferenceInDays(
            new Date(nextAvailableDayWithSlots.date),
            today,
          );

          acc.push({
            date: nextAvailableDayWithSlots.date,
            days: nextDayWithSlotInDays,
          });

          return acc;
        },
        [],
      ) ?? [];

    const currentZipcode = selectedAddress?.postalCode ?? '';
    const closestSlotOverall =
      closestSlots.length > 0
        ? closestSlots.reduce((closest, current) =>
            current.days < closest.days ? current : closest,
          )
        : null;
    const nextDayWithSlotInDays = closestSlotOverall?.days ?? null;

    if (
      lastTrackedSlotRef.current === nextDayWithSlotInDays ||
      lastTrackedZipcodeRef.current === currentZipcode
    ) {
      return;
    }

    trackEvent({
      type: 'LFDisplayDeliveryOption',
      data: {
        courier_first_available_slot: hasCourier ? nextDayWithSlotInDays : null,
        zipcode: currentZipcode,
      },
    });

    lastTrackedSlotRef.current = nextDayWithSlotInDays;
    lastTrackedZipcodeRef.current = currentZipcode;
  }, [
    slots,
    shippingMethodSelectionContent,
    findEligibleShippingMethod,
    selectedAddress?.postalCode,
  ]);

  const renderShippingMethodSelectors = useMemo(
    () =>
      shippingMethodSelectionContent?.shippingMethods.map(
        ({ shippingMethod }, index) => {
          const { shippingMethodType, title, subtitle, description, labels } =
            shippingMethod.data;

          const eligibleShippingMethod =
            findEligibleShippingMethod(shippingMethodType);
          const isEligible = Boolean(eligibleShippingMethod);

          if (!isEligible || !eligibleShippingMethod) return null;

          return (
            <Fragment key={index}>
              <ShippingMethodSelector
                shippingMethodType={shippingMethodType}
                carrierId={eligibleShippingMethod.carrierId ?? ''}
                title={title}
                subtitle={subtitleSerializer(subtitle, slots)}
                description={descriptionSerializer(
                  eligibleShippingMethod.configurations ?? [],
                  description,
                )}
                labels={labels}
                eligibleShippingMethod={eligibleShippingMethod}
              />
              <Spacer />
            </Fragment>
          );
        },
      ),
    [
      shippingMethodSelectionContent,
      slots,
      shippingMethods,
      descriptionSerializer,
      subtitleSerializer,
      findEligibleShippingMethod,
    ],
  );

  return (
    <>
      <DefaultPanelHeader
        onClose={closePanel}
        onGoBack={() =>
          send({
            type: EVENTS.GO_TO_STATE_ADDRESS_SELECTOR,
          })
        }
      />
      <SidePanel.SidePanelBody UNSAFE_className="!tw-px-4 !tw-py-6">
        <p className="tw-lf-body-regular-sm tw-mb-6">
          {tPanel.rich('subtitle', {
            selectedAddress:
              selectedAddress?.label ??
              (selectedAddress?.street &&
                selectedAddress?.postalCode &&
                selectedAddress?.city &&
                `${selectedAddress.street}, ${selectedAddress.postalCode} ${selectedAddress.city}`),
            span: (children) => (
              <span className="tw-lf-body-bold-sm">{children}</span>
            ),
          })}
        </p>
        {machineValue === DeliveryBarState.FETCH_SHIPPING_METHODS_ERROR ? (
          <InformativeBox
            UNSAFE_className="tw-mb-6"
            backgroundColor="var(--clr-secondary-2)"
          >
            {tPanel('fetchShippingMethodsError')}
          </InformativeBox>
        ) : (
          renderShippingMethodSelectors
        )}
        <SlicesContainer
          layout={SlicesContainerLayoutType.INSIDE}
          pageContents={shippingMethodSelectionContent?.slices}
          UNSAFE_className={`
              [&_.slice-container:not(:last-child)(.rich-text-zone)]:tw-mb-6
              `}
        />
      </SidePanel.SidePanelBody>
    </>
  );
};
